import { useState, type FormEvent } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useDelai, useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import {
  Badge,
  Champ,
  Chargement,
  EnTetePage,
  Erreur,
  Modale,
  Tableau,
  type Colonne,
} from '../components/Elements';
import { PiedFormulaire } from './ReferentielsPages';
import type { Etablissement, TypeEtablissement } from '../types';

const TYPES: { code: TypeEtablissement; libelle: string }[] = [
  { code: 'creche', libelle: 'Crèche' },
  { code: 'maternelle', libelle: 'Maternelle' },
  { code: 'elementaire', libelle: 'Élémentaire' },
  { code: 'college', libelle: 'Collège' },
  { code: 'lycee', libelle: 'Lycée' },
];
const LIBELLE_TYPE = Object.fromEntries(TYPES.map((t) => [t.code, t.libelle])) as Record<
  TypeEtablissement,
  string
>;

/**
 * Etablissements scolaires (2026-10-13) : le referentiel du champ
 * « ecole frequentee ». Partage entre les ecoles, sans annee. Une ligne
 * entree depuis une fiche arrive NON VALIDEE : la Direction la relit,
 * la complete (types, statut, arrondissement) et la valide.
 */
export function EtablissementsPage() {
  const { peut } = useAuth();
  const [saisie, setSaisie] = useState('');
  const recherche = useDelai(saisie);
  const [nonValides, setNonValides] = useState(false);
  const [modale, setModale] = useState<{ ouverte: boolean; etablissement: Etablissement | null }>({
    ouverte: false,
    etablissement: null,
  });
  const peutGerer = peut('etablissement.gerer');

  const liste = useRessource(
    () => api.get<Etablissement[]>('/api/etablissements', { recherche: recherche || undefined, nonValides }),
    [recherche, nonValides],
  );

  const valider = async (e: Etablissement) => {
    try {
      await api.patch(`/api/etablissements/${e.id}`, { valide: !e.valide });
      liste.recharger();
    } catch (cause) {
      window.alert(cause instanceof ErreurApi ? cause.message : 'Opération impossible.');
    }
  };

  const colonnes: Colonne<Etablissement>[] = [
    { cle: 'nom', entete: 'Établissement', rendu: (e) => <span className="font-medium">{e.nom}</span> },
    {
      cle: 'types',
      entete: 'Types',
      rendu: (e) => (
        <span className="flex flex-wrap gap-1">
          {e.types.map((t) => (
            <Badge key={t}>{LIBELLE_TYPE[t]}</Badge>
          ))}
          {e.types.length === 0 && <span className="text-xs text-encre-400">—</span>}
        </span>
      ),
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (e) =>
        e.statut ? (
          <Badge ton={e.statut === 'public' ? 'neutre' : 'contour'}>
            {e.statut === 'public' ? 'public' : 'privé'}
          </Badge>
        ) : (
          <span className="text-xs text-encre-400">—</span>
        ),
    },
    {
      cle: 'arrondissement',
      entete: 'Arrondissement',
      rendu: (e) => <span className="text-encre-600">{e.arrondissement ?? '—'}</span>,
    },
    {
      cle: 'fiches',
      entete: 'Fiches',
      classe: 'text-right',
      rendu: (e) => <span className="tabulaire">{e.nb_personnes}</span>,
    },
    {
      cle: 'valide',
      entete: 'Validé',
      rendu: (e) =>
        e.valide ? <Badge ton="succes">validé</Badge> : <Badge ton="alerte">à valider</Badge>,
    },
    {
      cle: 'actions',
      entete: '',
      classe: 'text-right',
      rendu: (e) =>
        peutGerer ? (
          <button
            type="button"
            className="text-sm text-encre-500 underline hover:text-encre-900"
            onClick={(evenement) => {
              evenement.stopPropagation();
              void valider(e);
            }}
          >
            {e.valide ? 'Invalider' : 'Valider'}
          </button>
        ) : null,
    },
  ];

  return (
    <>
      <EnTetePage
        titre="Établissements scolaires"
        description="Le référentiel du champ « École fréquentée » des fiches. Une école saisie sur une fiche et inconnue ici y entre « à valider » : complétez ses types, son statut et son arrondissement, puis validez-la. Renommer un établissement renomme le texte sur les fiches qui le portent."
        actions={
          peutGerer ? (
            <button
              type="button"
              className="bouton-principal"
              onClick={() => setModale({ ouverte: true, etablissement: null })}
            >
              Nouvel établissement
            </button>
          ) : null
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-4">
        <input
          className="champ w-72"
          placeholder="Rechercher…"
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
        />
        <label className="inline-flex items-center gap-2 text-sm text-encre-600">
          <input type="checkbox" checked={nonValides} onChange={(e) => setNonValides(e.target.checked)} />
          Seulement les établissements à valider
        </label>
      </div>

      {liste.erreur && <Erreur message={liste.erreur} onReessayer={liste.recharger} />}
      {liste.chargement ? (
        <Chargement />
      ) : (
        <div className="carte">
          <Tableau
            colonnes={colonnes}
            lignes={liste.donnees ?? []}
            cleDe={(e) => e.id}
            messageVide="Aucun établissement."
            onLigneCliquee={peutGerer ? (e) => setModale({ ouverte: true, etablissement: e }) : undefined}
          />
        </div>
      )}

      <FormulaireEtablissement
        etat={modale}
        onFermer={() => setModale({ ouverte: false, etablissement: null })}
        onEnregistre={() => {
          setModale({ ouverte: false, etablissement: null });
          liste.recharger();
        }}
      />
    </>
  );
}

function FormulaireEtablissement({
  etat,
  onFermer,
  onEnregistre,
}: {
  etat: { ouverte: boolean; etablissement: Etablissement | null };
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const [valeurs, setValeurs] = useState({
    nom: '',
    types: [] as TypeEtablissement[],
    statut: '' as '' | 'public' | 'prive',
    arrondissement: '',
    valide: false,
  });
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [initialisePour, setInitialisePour] = useState<string | null>(null);

  const cle = etat.ouverte ? (etat.etablissement?.id ?? 'nouveau') : null;
  if (cle && cle !== initialisePour) {
    setInitialisePour(cle);
    setValeurs({
      nom: etat.etablissement?.nom ?? '',
      types: etat.etablissement?.types ?? [],
      statut: etat.etablissement?.statut ?? '',
      arrondissement: etat.etablissement?.arrondissement ?? '',
      // Un etablissement cree a l'ecran par la Direction est valide d'emblee.
      valide: etat.etablissement?.valide ?? true,
    });
    setErreur(null);
  }
  if (!etat.ouverte && initialisePour !== null) setInitialisePour(null);

  const basculerType = (code: TypeEtablissement) =>
    setValeurs({
      ...valeurs,
      types: valeurs.types.includes(code)
        ? valeurs.types.filter((t) => t !== code)
        : [...valeurs.types, code],
    });

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      const corps = {
        nom: valeurs.nom,
        types: valeurs.types,
        statut: valeurs.statut || null,
        arrondissement: valeurs.arrondissement || null,
        valide: valeurs.valide,
      };
      if (etat.etablissement) {
        await api.patch(`/api/etablissements/${etat.etablissement.id}`, corps);
      } else {
        await api.post('/api/etablissements', corps);
      }
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const supprimer = async () => {
    if (!etat.etablissement || !window.confirm(`Supprimer « ${etat.etablissement.nom} » ?`)) return;
    try {
      await api.delete(`/api/etablissements/${etat.etablissement.id}`);
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Suppression impossible.');
    }
  };

  return (
    <Modale
      titre={etat.etablissement ? `Modifier ${etat.etablissement.nom}` : 'Nouvel établissement'}
      ouverte={etat.ouverte}
      onFermer={onFermer}
    >
      <form onSubmit={soumettre} className="space-y-4">
        <Champ
          libelle="Nom"
          aide="Préfixé par le type : « École » (maternelle, élémentaire), « Collège », « Lycée », « Crèche » — « Établissement » quand il en cumule plusieurs."
        >
          <input
            className="champ"
            value={valeurs.nom}
            onChange={(e) => setValeurs({ ...valeurs, nom: e.target.value })}
            required
            autoFocus
          />
        </Champ>

        <div>
          <span className="etiquette">Types</span>
          <div className="flex flex-wrap gap-4 text-sm">
            {TYPES.map((t) => (
              <label key={t.code} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={valeurs.types.includes(t.code)}
                  onChange={() => basculerType(t.code)}
                />
                {t.libelle}
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Champ libelle="Statut">
            <select
              className="champ"
              value={valeurs.statut}
              onChange={(e) => setValeurs({ ...valeurs, statut: e.target.value as '' | 'public' | 'prive' })}
            >
              <option value="">Non renseigné</option>
              <option value="public">Public</option>
              <option value="prive">Privé</option>
            </select>
          </Champ>
          <Champ libelle="Arrondissement ou commune" aide="« Paris 2e », « Boulogne-Billancourt ».">
            <input
              className="champ"
              value={valeurs.arrondissement}
              onChange={(e) => setValeurs({ ...valeurs, arrondissement: e.target.value })}
            />
          </Champ>
        </div>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={valeurs.valide}
            onChange={(e) => setValeurs({ ...valeurs, valide: e.target.checked })}
          />
          <span>
            Validé
            <span className="mt-0.5 block text-xs text-encre-400">
              Relu par la Direction : nom, types, statut et lieu sont justes.
            </span>
          </span>
        </label>

        {etat.etablissement && etat.etablissement.nb_personnes === 0 && (
          <button type="button" className="text-sm text-red-700 underline" onClick={() => void supprimer()}>
            Supprimer cet établissement (aucune fiche ne le porte)
          </button>
        )}

        <PiedFormulaire erreur={erreur} enCours={enCours} onFermer={onFermer} />
      </form>
    </Modale>
  );
}
